import { defineComponent, PropType } from "vue";

import { COMPONENT_NAME } from "./attributes";
import { HistoryTableRow } from "./row";
import { HistoryTableRowApplication } from "./row-application";
import { HistoryTableRowCrypto } from "./row-crypto";

import { UIButton } from "@/components/ui";
import { DepositWithdrawalHistoryResponse } from "@/shared/repository/modules/deposit-withdrawal/repo";
import { HistoryFiatCryptoTab, HistoryTab } from "@/shared/constants/enums";
import { ApplicationHistoryResponse } from "@/shared/repository/modules/application/repo";
import { WalletHistoryResponse } from "@/shared/repository/modules/wallet/repo";

type HistoryTableRowComponent =
  | typeof HistoryTableRow
  | typeof HistoryTableRowApplication
  | typeof HistoryTableRowCrypto;

export interface HistoryTableColumn {
  id: number;
  label: string;
}

export default defineComponent({
  name: COMPONENT_NAME,
  components: {
    HistoryTableRow,
    HistoryTableRowApplication,
    UIButton,
  },
  props: {
    history: {
      type: <
        PropType<
          | DepositWithdrawalHistoryResponse[]
          | ApplicationHistoryResponse[]
          | WalletHistoryResponse[]
        >
      >Array,
      default: () => [],
    },
    lastFetchCount: {
      type: Number,
      default: 0,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  emits: {
    depositWithdrawal: null,
    application: null,
    crypto: null,
    remove: null,
    showMore: null,
  },
  data() {
    return {
      historyRepo: this.$projectServices.historyRepo,
      showAll: false,
      itemsLimit: 20,
    };
  },
  watch: {
    "history.length"() {
      if (this.showAll && this.filteredRows.length > this.itemsLimit) {
        this.showAll = false;
      }
    },
  },
  computed: {
    displayedSearchValue(): string {
      return this.historyRepo.search.value;
    },

    displayedColumns(): HistoryTableColumn[] {
      switch (this.historyRepo.tab) {
        case HistoryTab.depositWithdrawal:
          switch (this.historyRepo.tabFiatCrypto) {
            case HistoryFiatCryptoTab.fiat:
              return [
                "Дата и время",
                "Тип",
                "Сумма",
                "Валюта",
                "Комиссия",
                "Статус",
              ].map((column, columnIndex) => ({
                id: columnIndex,
                label: column,
              }));
            case HistoryFiatCryptoTab.crypto:
              return [
                "Дата и время",
                "Тип",
                "Сумма",
                "Валюта",
                "Статус",
                "Кошелек",
              ].map((column, columnIndex) => ({
                id: columnIndex,
                label: column,
              }));
            default:
              return [];
          }
          break;
        case HistoryTab.application:
          return [
            "Дата и время",
            "Операция",
            "Цена исполнения",
            "Объем сделки",
            "Комиссия",
            "№ сделки",
            "Статус",
            "Этап",
          ].map((column, columnIndex) => ({ id: columnIndex, label: column }));
        default:
          return [];
      }
    },

    displayedSmallColumns(): HistoryTableColumn[] {
      return [
        {
          id: 0,
          label: "Сумма",
        },
        {
          id: 1,
          label: "Валюта",
        },
        {
          id: 2,
          label: "Дата",
        },
        {
          id: 3,
          label: "Статус",
        },
      ];
    },

    displayedRow(): HistoryTableRowComponent {
      switch (this.historyRepo.tab) {
        case HistoryTab.depositWithdrawal:
          switch (this.historyRepo.tabFiatCrypto) {
            case HistoryFiatCryptoTab.fiat:
              return HistoryTableRow;
            case HistoryFiatCryptoTab.crypto:
              return HistoryTableRowCrypto;
            default:
              return HistoryTableRow;
          }
          break;
        case HistoryTab.application:
          return HistoryTableRowApplication;
        default:
          return HistoryTableRow;
      }
    },

    filteredRows():
      | DepositWithdrawalHistoryResponse[]
      | ApplicationHistoryResponse[]
      | WalletHistoryResponse[] {
      switch (this.historyRepo.tab) {
        case HistoryTab.depositWithdrawal:
          switch (this.historyRepo.tabFiatCrypto) {
            case HistoryFiatCryptoTab.fiat:
              return (this.history as DepositWithdrawalHistoryResponse[])
                .slice()
                .sort(
                  (a, b) =>
                    new Date(b.created_at).getTime() -
                    new Date(a.created_at).getTime()
                )
                .filter((item) =>
                  `$${item.created_at} ${item.amount} ${item.currency.name}`
                    .toLowerCase()
                    .includes(this.displayedSearchValue.toLowerCase())
                ) as DepositWithdrawalHistoryResponse[];
            case HistoryFiatCryptoTab.crypto:
              return (this.history as WalletHistoryResponse[])
                .slice()
                .sort(
                  (a, b) =>
                    new Date(b.created_at).getTime() -
                    new Date(a.created_at).getTime()
                )
                .filter((item) =>
                  `$${item.created_at} ${item.amount} ${item.coin}`
                    .toLowerCase()
                    .includes(this.displayedSearchValue.toLowerCase())
                ) as WalletHistoryResponse[];
            default:
              return [];
          }
          break;
        case HistoryTab.application:
          return (this.history as ApplicationHistoryResponse[])
            .slice()
            .sort(
              (a, b) =>
                new Date(b.created_at).getTime() -
                new Date(a.created_at).getTime()
            )
            .filter((item) =>
              `$${item.created_at} ${item.amount} ${item.currency_from.name} ${item.currency_to.name}`
                .toLowerCase()
                .includes(this.displayedSearchValue.toLowerCase())
            ) as ApplicationHistoryResponse[];
        default:
          return [];
      }
    },

    displayedRows():
      | DepositWithdrawalHistoryResponse[]
      | ApplicationHistoryResponse[]
      | WalletHistoryResponse[] {
      // if (this.showAll) {
      return this.filteredRows;
      // }
      // return this.filteredRows.slice(0, this.itemsLimit);
    },

    hasMoreItems(): boolean {
      if (this.lastFetchCount > 0) {
        return this.lastFetchCount >= this.historyRepo.filter.per_page;
      }

      const isPageFull =
        this.history.length >= this.historyRepo.filter.per_page &&
        this.history.length ===
          this.historyRepo.filter.page * this.historyRepo.filter.per_page;

      return isPageFull;
    },

    isApplication(): boolean {
      return this.historyRepo.tab === HistoryTab.application;
    },
  },
  methods: {
    handleDepositWithdrawal(
      depositWithdrawal: DepositWithdrawalHistoryResponse
    ): void {
      this.$emit("depositWithdrawal", depositWithdrawal);
    },

    handleApplication(application: ApplicationHistoryResponse): void {
      this.$emit("application", application);
    },
    handleRemove(id: number): void {
      this.$emit("remove", id);
    },
    handleShowMore(): void {
      if (this.loading) {
        return;
      }
      this.$emit("showMore");
      this.showAll = true;
    },
  },
});
